import styles from '../../routes/Legal.module.css';
import { SectionWrapper } from '../shared/SectionWrapper';
import { useSEO } from '../../hooks/useSEO';
import type { LegalDocumentContent } from '../../content/legal/types';

type Props = {
  content: LegalDocumentContent;
  platform: 'ios' | 'android' | 'app';
  seoDescription: string;
  seoKeywords: string;
};

const platformLabel = {
  ios: 'iOS App',
  android: 'Android App',
  app: 'LuvLust App',
} as const;

function renderParagraph(text: string, key: number) {
  if (text.includes('@') && !text.startsWith('http')) {
    return (
      <p key={key}>
        <a href={`mailto:${text}`}>{text}</a>
      </p>
    );
  }
  if (text.startsWith('http')) {
    return (
      <p key={key}>
        <a href={text} target="_blank" rel="noopener noreferrer">
          {text}
        </a>
      </p>
    );
  }
  return <p key={key}>{text}</p>;
}

export function LegalDocument({
  content,
  platform,
  seoDescription,
  seoKeywords,
}: Props) {
  useSEO({
    title: `${content.title} — ${platformLabel[platform]}`,
    description: seoDescription,
    keywords: seoKeywords,
  });

  return (
    <SectionWrapper>
      <article className={styles.legal}>
        <h1 className={`display ${styles.title}`}>{content.title}</h1>
        <p className={styles.date}>{platformLabel[platform]}</p>
        {content.effectiveDate ? (
          <p className={styles.date}>{content.effectiveDate}</p>
        ) : null}
        {content.intro ? <p>{content.intro}</p> : null}

        {content.sections.map((section) => (
          <section key={section.title}>
            <h2>{section.title}</h2>
            {section.paragraphs?.map((paragraph, index) =>
              renderParagraph(paragraph, index),
            )}
            {section.subsections?.map((subsection) => (
              <div key={subsection.title}>
                <h3>{subsection.title}</h3>
                <p>{subsection.content}</p>
              </div>
            ))}
            {section.listItems ? (
              <ul>
                {section.listItems.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            ) : null}
          </section>
        ))}

        {content.consent ? (
          <p className={styles.consent}>{content.consent}</p>
        ) : null}
      </article>
    </SectionWrapper>
  );
}
